/* ─────────────────────────────────────────────────────────────
   FAIR-VALUE BUILDER
   GET /api/v1/stocks/{ticker}/fair-value/custom?anchors=...

   Caller picks any combination of the four anchors; the API returns their
   mean. Read-only — nothing is stored. Cached server-side for 15 minutes, so
   re-querying on every toggle is cheap.

   ⚠ Omitting the `anchors` param means "use the engine's default trio", so an
   empty selection would silently return the trio rather than nothing. The
   picker therefore refuses to let the last anchor be deselected.

   Renders a BARE section (no page wrapper) so it drops into a host tab that
   already owns its padding.
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { getCustomFairValue } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import { DEFAULT_ANCHORS, anchorLabel, decimalAsPct, fmtAsOf } from "../../../config/valuation";
import { fmtPct } from "../../../config/earningsNote";
import {
  SectionTitle, TileGrid, Banner, Scroller, Table, Th, Td, Tl,
} from "./earningsNoteUI";
import { AnchorPicker } from "./valuationWidgets";

const upsideTone = (v) => (v == null ? "muted" : Number(v) >= 0 ? "pos" : "neg");

export const FairValueBuilder = ({ ticker }) => {
  const [selected, setSelected] = useState([...DEFAULT_ANCHORS]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setProblem(null);
      const res = await getCustomFairValue(ticker, selected);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else if (res?.badRequest) setProblem(res.detail);
      else if (!res?.notFound) setProblem("Could not calculate a custom fair value.");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker, selected]);

  // in_mean === false → anchor selected but null for this stock
  const anchorState = {};
  (data?.anchors ?? []).forEach((a) => {
    anchorState[a.name] = a.in_mean;
  });

  const skipped = (data?.anchors ?? []).filter((a) => a.in_mean === false);

  const custom = data?.custom_fair_value ?? null;
  const stored = data?.stored_fair_value ?? null;
  const differsFromStored =
    custom != null && stored != null && Math.abs(custom - stored) > 0.005;

  const tiles = data
    ? [
        custom != null
          ? {
              label: "Custom fair value",
              value: formatCurrency(custom),
              hint: `mean of ${data.anchor_count ?? selected.length} anchor${
                (data.anchor_count ?? selected.length) === 1 ? "" : "s"
              }`,
            }
          : null,
        data.price_used != null
          ? { label: "Price used", value: formatCurrency(data.price_used) }
          : null,
        data.upside_to_custom_fv != null
          ? {
              label: "Upside to custom FV",
              value: fmtPct(data.upside_to_custom_fv, { decimals: 1 }),
              tone: upsideTone(data.upside_to_custom_fv),
            }
          : null,
        data.entry_price != null
          ? { label: "Entry price", value: formatCurrency(data.entry_price), tone: "pos" }
          : null,
        data.exit_price != null
          ? { label: "Exit price", value: formatCurrency(data.exit_price), tone: "neg" }
          : null,
        data.band != null
          ? { label: "Band", value: decimalAsPct(data.band, 1), hint: "applied to the custom mean" }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <>
      <SectionTitle caption="pick the anchors — the API returns their mean; nothing is stored">
        Fair-value builder
      </SectionTitle>

      <AnchorPicker
        selected={selected}
        onChange={setSelected}
        anchorState={anchorState}
        disabled={loading}
      />

      <div
        style={{
          marginTop: "var(--spacing-md)",
          opacity: loading ? 0.55 : 1,
          transition: "var(--transition-fast)",
        }}
      >
        {problem ? (
          <Banner kind="warning">{problem}</Banner>
        ) : (
          <>
            <TileGrid tiles={tiles} min={150} />

            {data?.anchors?.length > 0 && (
              <Scroller style={{ marginTop: "var(--spacing-sm)" }}>
                <Table>
                  <thead>
                    <tr>
                      <Th left>Anchor</Th>
                      <Th>Value</Th>
                      <Th left>In mean</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.anchors.map((a) => (
                      <tr key={a.name}>
                        <Tl>{anchorLabel(a.name)}</Tl>
                        <Td bold>{a.value != null ? formatCurrency(a.value) : "—"}</Td>
                        <Td left tone={a.in_mean ? "pos" : "muted"}>
                          {a.in_mean ? "Yes" : "Skipped — no value for this stock"}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Scroller>
            )}

            {skipped.length > 0 && (
              <Banner kind="warning" style={{ marginTop: "var(--spacing-sm)" }}>
                {skipped.map((a) => anchorLabel(a.name)).join(", ")}{" "}
                {skipped.length === 1 ? "is" : "are"} selected but unavailable for {ticker}, so{" "}
                {skipped.length === 1 ? "it was" : "they were"} left out of the mean.
              </Banner>
            )}

            {differsFromStored && (
              <Banner kind="plain" style={{ marginTop: "var(--spacing-sm)" }}>
                The engine's stored fair value is <strong>{formatCurrency(stored)}</strong> (default
                trio: analyst target, intrinsic value, own-history FV). Your selection gives{" "}
                <strong>{formatCurrency(custom)}</strong>.
              </Banner>
            )}

            {data?.calculated_at && (
              <div
                style={{
                  marginTop: "var(--spacing-sm)",
                  color: "var(--color-text-muted)",
                  fontSize: "var(--font-size-xs)",
                }}
              >
                Calculated {fmtAsOf(data.calculated_at)}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default FairValueBuilder;
